import { useState, useEffect, useRef, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  Menu,
  LogOut,
  Wrench,
  User,
  Home as HomeIcon,
  Search as SearchIcon,
  Mail as MailIcon,
  Info as InfoIcon,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GolfAuthNavLogo, GolfBagIcon, ThemeQuickToggle } from '@golfauth/ui';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useCustomerStore } from '../../store/customerStore';
import { useAuthStore } from '../../store/authStore';
import { goToCore } from '../../lib/goToCore';
import { useProfileStrengthScore } from '../../hooks/useProfileStrengthScore';

// In the Pro Capacitor app the audience is pro-only — sign-in always
// targets /pro/login (never /customer/login) and logo taps go to
// /pro/admin (or /pro/login when signed out) rather than the
// golfer marketing hub. Web behaviour matches.
const IS_NATIVE = Capacitor.isNativePlatform();
import { LookupCodePill } from './LookupCodePill';

// Anonymous visitors get NO center-nav links — only the wordmark on
// the left, a single "Sign in" text link on the right, and a
// hamburger button that opens a universal menu (Home, About, Find a
// Professional, Contact). The hamburger replaces the previous "find
// these in the footer" pattern: visitors expect a menu in PWAs, and
// discoverability of /about + /find-professional + /contact matters
// for the marketing site even though they don't deserve horizontal-nav
// real estate up here.

// Signed-in Pro surfaces — kept in one list so the desktop strip and
// the hamburger stay in sync. Profile deep-links into Admin's profile
// tab (the stub /pro/profile page is not the real settings UI).
const privateNavLinks = [
  { name: 'Select Golfer', path: '/pro/select-golfer' },
  { name: 'Configurations', path: '/pro/configurations' },
  { name: 'Profile', path: '/pro/admin?activeTab=profile' },
  { name: 'Admin', path: '/pro/admin' },
];

const fittingNavItems = [
  { name: 'Dashboard', path: '/golfer/dashboard' },
  { name: 'Profile', path: '/golfer/profile' },
  { name: 'Configuration History', path: '/golfer/config-history' },
  { name: 'Current Quotes', path: '/golfer/current-quotes' },
  { name: 'Orders', path: '/golfer/orders' },
  { name: 'Messages', path: '/golfer/messages' },
];

const Navbar = memo(function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const themeToggle = (
    <>
      <hr className="border-gray-200 dark:border-white/10 my-1" />
      <ThemeQuickToggle
        resolvedTheme={resolvedTheme}
        followsSystem={theme === 'system'}
        onToggle={setTheme}
        className="px-4 text-base hover:bg-gray-100 dark:hover:bg-white/10"
      />
    </>
  );

  // Close the hamburger dropdown when the user clicks outside it
  // (anywhere on the page, including main content), and when they press
  // Escape. menuRef wraps both the button and the dropdown panel, so a
  // click on the toggle button itself doesn't trigger the outside-click
  // close (which would race with the toggle and immediately re-close it).
  useEffect(() => {
    if (!isMenuOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsMenuOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  // Close on route change — without this, navigating via a link inside
  // the dropdown leaves the panel open after the destination renders.
  // (Most links already call setIsMenuOpen(false) on click, but logout
  // and a few keyboard / programmatic paths don't.)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const { selectCustomer } = useCustomerStore();
  const { professional, customer, logout } = useAuthStore();
  const isFittingActive = location.pathname.startsWith('/golfer') && location.pathname !== '/golfer';

  // Brand-identity routing: the wordmark switches to "GolfAuth Pro" on
  // the pro-side of the platform so logged-in users (and anonymous
  // visitors who landed on /pro) see the right product identity. Plain
  // "GolfAuth" stays on consumer marketing + golfer-facing routes.
  // Pro route prefixes — every Pro auth + app route now nests under
  // /pro/*, so a single prefix covers them all. /golfer kept here to
  // preserve the pre-existing behaviour where golfer-fitting routes
  // also surface the pro wordmark variant (consistent with the Pro
  // sidebar treatment).
  const PRO_ROUTE_PREFIXES = [
    '/pro',
    '/golfer',
  ];
  const isProRoute = PRO_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Marketing pages — the only place the "GolfAuth Pro" cross-promotion
  // link should appear. Anywhere else (golfer routes, pro routes, auth
  // pages, checkout, etc.) it's noise. Exact "/" is split out from the
  // prefix list because "/" matches every path with .startsWith.
  const MARKETING_EXACT_ROUTES = new Set(['/']);
  const MARKETING_PREFIX_ROUTES = [
    '/about',
    '/pro',
    '/find-professional',
    '/contact',
    '/stats',
    '/glossary',
    '/verification-101',
    '/privacy',
    '/terms',
    '/security',
    '/cookies',
    '/delete-account',
    '/team',
  ];
  const isMarketingRoute =
    MARKETING_EXACT_ROUTES.has(location.pathname) ||
    MARKETING_PREFIX_ROUTES.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
  // In the Pro Capacitor app every surface is pro — force the pro logo
  // variant and a pro-only logo target. On web the existing
  // pro-vs-consumer split stays.
  const logoVariant: 'default' | 'pro' = IS_NATIVE || professional || isProRoute ? 'pro' : 'default';
  // Signed-in Pros stay in the Pro app (admin home). Never send them
  // to marketing `/` or `/pro` — those surfaces are for golfers / anon
  // and `/` can bounce a standalone PWA into Core.
  const logoLinkTarget = IS_NATIVE
    ? (professional ? '/pro/admin' : '/pro/login')
    : professional
      ? '/pro/admin'
      : logoVariant === 'pro'
        ? '/pro'
        : '/';
  // Anon "Sign in" target. Web routes by audience (pro routes →
  // /pro/login; consumer → /customer/login). Native forces /pro/login.
  const signInTarget = IS_NATIVE ? '/pro/login' : (isProRoute ? '/pro/login' : '/customer/login');

  // Anon primary-CTA target for the persistent "Start free" button. Same
  // audience split as sign-in: pros on /pro get the pro trial signup,
  // every other marketing surface gets the golfer free-bag signup. The
  // button is the conversion anchor the marketing pages previously left
  // entirely to the hero — now it follows the visitor down the page.
  const isProSurface = location.pathname === '/pro' || location.pathname.startsWith('/pro/');
  const registerTarget = isProSurface
    ? '/pro/register?source=nav_cta'
    : '/customer/register?source=nav_cta';
  const registerLabel = isProSurface ? 'Start free trial' : 'Start free';

  const handleLogout = () => {
    // Capture the user type *before* we clear the auth store — once `logout()`
    // resolves, both `customer` and `professional` are null and we can't tell
    // them apart. Golfers go back to the public marketing home (`/`); professionals
    // stay on the admin sign-in page so they can sign back in quickly.
    const wasGolfer = !!customer;
    selectCustomer('');
    logout();
    // Native Pro app: there is no public `/` to land on, so logout
    // always returns to the pro sign-in screen regardless of audience.
    navigate(IS_NATIVE ? '/pro/login' : (wasGolfer ? '/' : '/pro/login'));
    setIsMenuOpen(false);
  };

  const getNavLinks = () => {
    if (customer) {
      return fittingNavItems;
    } else if (professional) {
      return privateNavLinks;
    }
    // Anonymous visitors get no center-nav links (see comment above).
    return [];
  };

  /** Used so ?activeTab=profile still highlights Profile vs Admin correctly. */
  const isActiveNavLink = (path: string) => {
    const base = path.split('?')[0];
    if (path.includes('activeTab=profile')) {
      return (
        location.pathname === '/pro/admin' &&
        new URLSearchParams(location.search).get('activeTab') === 'profile'
      );
    }
    if (base === '/pro/admin') {
      return (
        location.pathname === '/pro/admin' &&
        new URLSearchParams(location.search).get('activeTab') !== 'profile'
      );
    }
    return location.pathname === base;
  };

  const navLinks = getNavLinks();
  const profileStrengthScore = useProfileStrengthScore();
  const showProfileStrengthDot =
    profileStrengthScore !== undefined && profileStrengthScore < 85;

  return (
    <header
      className="fixed w-full z-50 backdrop-blur-sm border-b shadow-sm"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
        // Capacitor sets `contentInset: 'never'`, so the WKWebView paints
        // behind the iPhone notch / status bar. Pad the navbar's top by
        // the safe-area inset so the logo + hamburger don't render under
        // the hardware (where input is blocked and the camera covers
        // pixels). No-op on web — env(safe-area-inset-top) is 0px in a
        // normal browser.
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20 gap-3">
          <Link
            to={logoLinkTarget}
            className="shrink-0 flex items-baseline min-w-0 hover:opacity-90 transition-opacity"
            aria-label={logoVariant === 'pro' ? 'GolfAuth Pro home' : 'GolfAuth home'}
          >
            <GolfAuthNavLogo
              className="h-8 w-auto max-h-10 max-w-[min(180px,48vw)] object-contain object-left"
              variant={logoVariant}
            />
          </Link>

          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Nav selections — sit immediately left of the right
                cluster + hamburger so the whole nav reads as one
                grouped affordance on the right edge of the bar. */}
            <nav className="hidden md:flex md:items-center md:space-x-1">
              {customer ? (
                <>
                  <button
                    type="button"
                    onClick={goToCore}
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors px-4 py-2 rounded-lg",
                      "text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-white/10"
                    )}
                  >
                    <GolfBagIcon className="w-[1.2rem] h-[1.2rem] object-contain" />
                    My Bag
                  </button>
                  <Link
                    to="/golfer/dashboard"
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors px-4 py-2 rounded-lg",
                      "text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-white/10",
                      isFittingActive && "bg-brand-500/20 text-brand-400"
                    )}
                  >
                    <Wrench className="w-4 h-4" />
                    Fittings
                  </Link>
                  <Link
                    to="/golfer/profile"
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors px-4 py-2 rounded-lg",
                      "text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-white/10",
                      location.pathname === '/golfer/profile' && "bg-brand-500/20 text-brand-400"
                    )}
                    aria-label={showProfileStrengthDot ? 'Profile — complete your profile' : 'Profile'}
                  >
                    <span className="relative inline-flex">
                      <User className="w-4 h-4" />
                      {showProfileStrengthDot && (
                        <span className="absolute -top-0.5 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-[var(--color-surface)]" />
                      )}
                    </span>
                    Profile
                  </Link>
                </>
              ) : (
                navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={cn(
                      "text-sm font-medium transition-colors px-4 py-2 rounded-lg",
                      "text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-white/10",
                      isActiveNavLink(link.path) && "bg-brand-500/20 text-brand-400"
                    )}
                  >
                    {link.name}
                  </Link>
                ))
              )}
            </nav>

            <div className="hidden md:flex md:items-center md:space-x-3">
              {customer ? (
                // Signed-in golfer: keep only the lookup-code pill in
                // the right cluster. "Hi Name" + Sign Out moved to the
                // hamburger so the navbar reads cleaner. Sign Out lives
                // exclusively under the menu now.
                customer.lookup_code ? (
                  <LookupCodePill code={customer.lookup_code} />
                ) : null
              ) : professional ? (
                // Signed-in pro: nothing in the desktop right cluster;
                // Sign Out only available via hamburger.
                null
              ) : (
                // Anonymous: a Pricing link + a Sign in text link,
                // audience-appropriate target. Hero CTAs do the heavy
                // lifting. Pricing jumps to the in-page pricing matrix:
                // /pro#pricing when on /pro, /#pricing everywhere else.
                // Hidden in the Pro Capacitor wrap (no consumer surface).
                <>
                  {!IS_NATIVE && (
                    <Link
                      to={location.pathname === '/pro' ? '/pro#pricing' : '/#pricing'}
                      className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors px-3 py-2"
                    >
                      Pricing
                    </Link>
                  )}
                  <Link
                    to={signInTarget}
                    className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors px-3 py-2"
                  >
                    Sign in
                  </Link>
                  {!IS_NATIVE && (
                  <Link
                    to={registerTarget}
                    className="inline-flex items-center justify-center text-sm font-semibold text-white rounded-lg px-6 py-2.5 min-h-[44px] shadow-sm whitespace-nowrap transition-[filter] hover:brightness-110"
                    style={{ backgroundColor: 'var(--color-brand)' }}
                  >
                    {registerLabel}
                  </Link>
                  )}
                </>
              )}
            </div>

            {/* GolfAuth Pro cross-promo — only on marketing pages, only
                for anonymous visitors. Hidden when any user is signed
                in (golfer doesn't need to be pushed to Pro; pro is
                already in Pro), on every non-marketing route, and in
                the native Pro Capacitor shell. */}
            {!customer && !professional && !IS_NATIVE && isMarketingRoute && (
              <Link
                to="/pro"
                className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors px-2 py-2 whitespace-nowrap"
              >
                GolfAuth Pro
              </Link>
            )}

            {/* Hamburger + dropdown — always visible (mobile + desktop).
                Wrapped in a `relative` container so the dropdown can be
                positioned absolutely below the button and right-aligned
                to its trailing edge. menuRef covers both so the
                click-outside detector (see useEffect) correctly treats
                button clicks as inside-clicks. */}
            <div ref={menuRef} className="relative">
              <button
                type="button"
                className="p-2 text-gray-600 dark:text-gray-300 shrink-0"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                aria-haspopup="menu"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>

              {isMenuOpen && (
                <div
                  role="menu"
                  aria-label="Main menu"
                  className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-lg shadow-lg border overflow-hidden z-50"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                  }}
                >
                  <nav className="flex flex-col p-2">
              {/* Signed-in Pro: stay inside the Pro app. No Home (/ → Core
                  for standalone PWAs), Find a Professional, or Contact —
                  those are golfer/marketing surfaces. Core remains a
                  separate app if the same email also has a golfer account. */}
              {professional ? (
                <>
                  {privateNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={cn(
                        'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                        'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                        isActiveNavLink(link.path) && 'bg-brand-500/20 text-brand-400',
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  {!IS_NATIVE && (
                    <Link
                      to="/about"
                      onClick={() => setIsMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                        'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                        location.pathname === '/about' && 'bg-brand-500/20 text-brand-400',
                      )}
                    >
                      <InfoIcon className="w-4 h-4" />
                      About
                    </Link>
                  )}
                  {themeToggle}
                  <hr className="border-gray-200 dark:border-white/10 my-1" />
                  <p className="py-2 px-4 text-sm text-gray-600 dark:text-gray-400">
                    Hi {professional.first_name}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 px-4 font-medium text-red-600 dark:text-error-400 text-left flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
              {/* Marketing links — always present on web for anon/golfer.
                  About / Find a Professional / Contact are hidden in the
                  Pro Capacitor shell. */}
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                  'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                  location.pathname === '/' && 'bg-brand-500/20 text-brand-400',
                )}
              >
                <HomeIcon className="w-4 h-4" />
                Home
              </Link>
              {!IS_NATIVE && (
                <Link
                  to="/about"
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                    location.pathname === '/about' && 'bg-brand-500/20 text-brand-400',
                  )}
                >
                  <InfoIcon className="w-4 h-4" />
                  About
                </Link>
              )}
              {!IS_NATIVE && (
                <Link
                  to="/find-professional"
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                    location.pathname === '/find-professional' && 'bg-brand-500/20 text-brand-400',
                  )}
                >
                  <SearchIcon className="w-4 h-4" />
                  Find a Professional
                </Link>
              )}
              {!IS_NATIVE && (
                <Link
                  to="/contact"
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10',
                    location.pathname === '/contact' && 'bg-brand-500/20 text-brand-400',
                  )}
                >
                  <MailIcon className="w-4 h-4" />
                  Contact
                </Link>
              )}

              {/* Logged-in golfer items — only on mobile, since the horizontal
                  nav already shows them at md+. Keeps the hamburger
                  tight on desktop and avoids duplication. */}
              {customer && (
                <div className="md:hidden flex flex-col space-y-2">
                  <hr className="border-gray-200 dark:border-white/10 my-1" />
                  <button
                    type="button"
                    className="flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
                    onClick={() => {
                      setIsMenuOpen(false);
                      goToCore();
                    }}
                  >
                    <GolfBagIcon className="w-[1.2rem] h-[1.2rem] object-contain" />
                    My Bag
                  </button>
                  <Link
                    to="/golfer/dashboard"
                    className={cn(
                      'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                      'text-gray-700 dark:text-gray-300',
                      location.pathname === '/golfer/dashboard' && 'bg-brand-500/20 text-brand-400',
                    )}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Wrench className="w-4 h-4" />
                    Fittings
                  </Link>
                  <Link
                    to="/golfer/profile"
                    className={cn(
                      'flex items-center gap-2 py-3 px-4 font-medium rounded-lg transition-colors',
                      'text-gray-700 dark:text-gray-300',
                      location.pathname === '/golfer/profile' && 'bg-brand-500/20 text-brand-400',
                    )}
                    onClick={() => setIsMenuOpen(false)}
                    aria-label={
                      showProfileStrengthDot ? 'Profile — complete your profile' : 'Profile'
                    }
                  >
                    <span className="relative inline-flex">
                      <User className="w-4 h-4" />
                      {showProfileStrengthDot && (
                        <span className="absolute -top-0.5 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-[var(--color-surface)]" />
                      )}
                    </span>
                    Profile
                  </Link>
                  {customer.lookup_code && (
                    <LookupCodePill code={customer.lookup_code} variant="full" />
                  )}
                </div>
              )}

              {/* Sign in / Sign out at the bottom. Sign Out lives
                  exclusively here on both mobile AND desktop — the
                  right cluster no longer carries the inline Sign Out
                  button. Sign in stays mobile-only because desktop
                  renders it inline in the right cluster for anon
                  visitors. */}
              {themeToggle}
              {customer ? (
                <>
                  <hr className="border-gray-200 dark:border-white/10 my-1" />
                  <p className="py-2 px-4 text-sm text-gray-600 dark:text-gray-400">
                    Hi {customer.first_name}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 px-4 font-medium text-red-600 dark:text-error-400 text-left flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="md:hidden">
                  <hr className="border-gray-200 dark:border-white/10 my-1" />
                  <Link
                    to={signInTarget}
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3 px-4 font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                  >
                    Sign in
                  </Link>
                  {!IS_NATIVE && (
                    <Link
                      to={registerTarget}
                      onClick={() => setIsMenuOpen(false)}
                      className="mt-1 block text-center py-3 px-4 font-semibold text-white rounded-lg transition-[filter] hover:brightness-110"
                      style={{ backgroundColor: 'var(--color-brand)' }}
                    >
                      {registerLabel}
                    </Link>
                  )}
                </div>
              )}
                </>
              )}
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
});

export default Navbar;